import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import uiActions from 'tradera-state/ui/actions';
import itemActions from 'tradera-state/items/actions';
import discoverActions from 'tradera-state/discover/actions';
import { LocalLoader } from 'tradera-components/loader';
import DialogModal from 'tradera-components/dialog/dialog-modal';
import { discounRates } from './discount-rates';
import { getItemDiscountQuota, postItemSettings } from '../../state/items/api';
import CloseIcon from '@tradera/blueprint/theme/icons/close-narrow.svg?jsx';
import ITEM_TYPES from 'tradera-constants/item-types';
import './item-settings.scss';

const calculateDiscount = (priceBeforeDiscount, discountRate) => {
    let price = Math.round(priceBeforeDiscount * (1 - discountRate / 100.0));
    if (price < 1) {
        return 1;
    }
    return price;
};

class ItemSettings extends Component {
    constructor(props) {
        super(props);
        const { item } = props;
        this.state = {
            itemSettings: {
                onDisplay: item.onDisplay,
                price: item.price,
                priceBeforeDiscount: item.priceBeforeDiscount,
                canHaveDiscount: [
                    ITEM_TYPES.PURE_BIN,
                    ITEM_TYPES.SHOP_ITEM
                ].includes(item.itemType),
                hasDiscount: item.discountRate > 0,
                discountRate: item.discountRate,
                onDisplayCount: 0,
                maxOnDisplayCount: 0,
                discountCount: 0,
                maxDiscountCount: 0
            },
            isFetching: false
        };
    }

    async getItemSettings() {
        const { closeItemSettings, item } = this.props;
        try {
            this.setState({
                isFetching: true
            });
            const itemSettings = await getItemDiscountQuota();
            const { data } = itemSettings;
            this.setState({
                itemSettings: {
                    ...this.state.itemSettings,
                    onDisplay: item.onDisplay,
                    price: item.price,
                    priceBeforeDiscount: item.priceBeforeDiscount,
                    canHaveDiscount: [
                        ITEM_TYPES.PURE_BIN,
                        ITEM_TYPES.SHOP_ITEM
                    ].includes(item.itemType),
                    hasDiscount: item.discountRate > 0,
                    discountRate: item.discountRate,
                    ...data
                },
                isFetching: false
            });
        } catch (error) {
            closeItemSettings();
        }
    }

    async saveItemSettings() {
        const {
            addItemOnDisplay,
            removeItemOnDisplay,
            item: { itemId },
            closeItemSettings,
            updateItem
        } = this.props;
        const {
            itemSettings: { onDisplay, discountRate, price }
        } = this.state;

        this.setState({
            isFetching: true
        });
        try {
            await postItemSettings(itemId, onDisplay, discountRate);

            updateItem(itemId, {
                onDisplay,
                discountRate,
                price
            });
            if (onDisplay) {
                addItemOnDisplay(itemId);
            } else {
                removeItemOnDisplay(itemId);
            }
            closeItemSettings();
        } catch (e) {
            closeItemSettings();
        }
    }

    handleOnDislayClick() {
        const {
            itemSettings: { onDisplayCount, onDisplay }
        } = this.state;
        const count = onDisplay ? onDisplayCount - 1 : onDisplayCount + 1;
        this.setState({
            itemSettings: {
                ...this.state.itemSettings,
                onDisplay: !onDisplay,
                onDisplayCount: count
            }
        });
    }

    handleDiscountChange(discountRate) {
        const {
            itemSettings: { discountCount, priceBeforeDiscount, hasDiscount }
        } = this.state;
        let count = discountCount;
        if (!hasDiscount && discountRate > 0) {
            count = count + 1;
        } else if (hasDiscount && discountRate === 0) {
            count = count - 1;
        }
        const price = calculateDiscount(priceBeforeDiscount, discountRate);
        this.setState({
            itemSettings: {
                ...this.state.itemSettings,
                price: price,
                discountRate: discountRate,
                hasDiscount: discountRate > 0 && priceBeforeDiscount !== price,
                discountCount: count
            }
        });
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.open &&
            this.props.item &&
            this.props.item !== prevProps.item
        ) {
            const getItemSettings = async () => {
                await this.getItemSettings();
            };
            getItemSettings();
        }
    }

    componentWillUnmount() {
        this.props.closeItemSettings();
    }

    render() {
        const { open, closeItemSettings } = this.props;

        const { isFetching, item = {}, itemSettings } = this.state;

        if (open !== 'open' || !item) {
            return null;
        }

        const {
            onDisplay,
            price,
            canHaveDiscount,
            hasDiscount,
            discountRate,
            onDisplayCount,
            maxOnDisplayCount,
            discountCount,
            maxDiscountCount
        } = itemSettings;

        const onDisplayDisabled =
            onDisplayCount === maxOnDisplayCount && !onDisplay;

        const setDiscountDisabled =
            discountCount === maxDiscountCount && !hasDiscount;

        return (
            <DialogModal
                mobileFullscreen={false}
                onDismiss={() => closeItemSettings()}>
                <div className="d-flex flex-column item-settings">
                    <div className="d-flex justify-content-center bg-gray-100 py-2 px-1">
                        <h3 className="mb-0">Verktyg för din Traderabutik</h3>
                        <CloseIcon
                            className="item-settings--close-button cursor-pointer icon ml-auto"
                            onClick={() => closeItemSettings()}
                        />
                    </div>
                    <div
                        className="container p-2"
                        style={{ minWidth: '440px' }}>
                        {isFetching ? (
                            <div
                                className="position-relative"
                                style={{ height: '130px' }}>
                                <LocalLoader />
                            </div>
                        ) : (
                            <Fragment>
                                <div className="row my-2">
                                    <div className="col-12">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="itemOnDisplay"
                                                id="itemOnDisplay"
                                                checked={onDisplay}
                                                disabled={onDisplayDisabled}
                                                onChange={() => {
                                                    this.handleOnDislayClick();
                                                }}
                                            />
                                            <label
                                                className="form-check-label ml-2"
                                                htmlFor="itemOnDisplay">
                                                {onDisplayDisabled ? (
                                                    <span className="text-danger">
                                                        {`Max antal utvalda object uppnått (${maxOnDisplayCount})`}
                                                    </span>
                                                ) : (
                                                    `Flytta produkten längst upp i listan (${maxOnDisplayCount -
                                                        onDisplayCount} av ${maxOnDisplayCount}
                                                kvar)`
                                                )}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                {canHaveDiscount && (
                                    <div className="bg-gray-200 p-2">
                                        <div className="row">
                                            <div className="col-12 my-1">
                                                <h4 className="mb-0">
                                                    Vill du lägga till rabatt
                                                    för denna produkt?
                                                </h4>
                                                <div
                                                    className={`${
                                                        setDiscountDisabled
                                                            ? 'text-danger'
                                                            : ''
                                                    }`}>
                                                    {`(${maxDiscountCount -
                                                        discountCount} av ${maxDiscountCount} kvar)`}
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <form>
                                                    <div className="form-group">
                                                        <select
                                                            className="form-control single-select form-control-rounded form-control-border-dark"
                                                            id="discountSelector"
                                                            value={discountRate}
                                                            disabled={
                                                                setDiscountDisabled
                                                            }
                                                            onChange={e => {
                                                                const discountRate =
                                                                    e.target
                                                                        .value;
                                                                this.handleDiscountChange(
                                                                    parseInt(
                                                                        discountRate
                                                                    )
                                                                );
                                                            }}>
                                                            {discounRates.map(
                                                                (
                                                                    option,
                                                                    index
                                                                ) => {
                                                                    return (
                                                                        <option
                                                                            key={
                                                                                index
                                                                            }
                                                                            value={
                                                                                option.value
                                                                            }>
                                                                            {
                                                                                option.name
                                                                            }
                                                                        </option>
                                                                    );
                                                                }
                                                            )}
                                                        </select>
                                                    </div>
                                                </form>
                                            </div>
                                            <div className="col-6 d-flex flex-column align-items-center">
                                                <h3
                                                    className={`mb-1 ${
                                                        hasDiscount
                                                            ? 'text-danger'
                                                            : ''
                                                    }`}>
                                                    {price} kr
                                                </h3>
                                                <div>
                                                    {hasDiscount
                                                        ? 'Rabatterat pris'
                                                        : 'Nuvarande pris'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="row my-2">
                                    <div className="col-12 d-flex flex-nowrap">
                                        <button
                                            className="btn btn-outline-danger btn-fluid mr-1"
                                            onClick={() => closeItemSettings()}>
                                            Avbryt
                                        </button>
                                        <button
                                            className="btn btn-primary btn-fluid ml-1"
                                            onClick={() =>
                                                this.saveItemSettings()
                                            }>
                                            Spara
                                        </button>
                                    </div>
                                </div>
                            </Fragment>
                        )}
                    </div>
                </div>
            </DialogModal>
        );
    }
}

ItemSettings.propTypes = {
    open: PropTypes.string,
    item: PropTypes.object
};

const mapDispatchToProps = dispatch => ({
    closeItemSettings: () => dispatch(uiActions.closeItemSettings()),
    updateItem: (itemId, item) =>
        dispatch(itemActions.updateItem({ itemId, item })),
    addItemOnDisplay: itemId =>
        dispatch(discoverActions.addItemOnDisplay({ itemId })),
    removeItemOnDisplay: itemId =>
        dispatch(discoverActions.removeItemOnDisplay({ itemId }))
});

const mapStateToProps = state => ({
    open: state.ui.itemSettings.open,
    item: state.ui.itemSettings.item
});

export default connect(mapStateToProps, mapDispatchToProps)(ItemSettings);
